<script lang="ts" setup>
import BaseCard from "@/components/BaseCard.vue";
import BaseContent from "@/components/BaseContent.vue";
import BaseSubContent from "@/components/BaseSubContent.vue";
import BaseSwitch from "@/components/BaseSwitch.vue";
import CharacterDomain from "@/components/CharacterDomain.vue";
import useCharacter from "@/composables/character";
import { Character } from "@/types/character";
import { PassiveSkill } from "@/types/passive-skill";
import { usePage } from "@inertiajs/vue3";
import { computed, ref } from "vue";

const page = usePage();

const props = defineProps<{
  character: Character;
  eza?: boolean;
}>();

const hasSeza = useCharacter(props.character).hasSeza;
const seza = ref(true);

const passiveSkill = computed<PassiveSkill>(() => {
  if (props.eza && hasSeza && !seza.value) {
    return props.character.extreme_z_awakenings[
      props.character.extreme_z_awakenings.length - 2
    ].passive_skill;
  }

  if ((props.eza && hasSeza && seza.value) || props.eza) {
    return props.character.extreme_z_awakenings[
      props.character.extreme_z_awakenings.length - 1
    ].passive_skill;
  }

  return props.character.passive_skill;
});

const effectDescriptionsItemized = computed(() => {
  const replacements = {
    up_g: "arrow01",
    down_r: "arrow02",
    down_y: "arrow03",
    down_g: "arrow04",
    once: "icon_01",
    forever: "icon_02",
  };

  return passiveSkill.value.description_itemized?.map((item: object) => {
    return {
      description: item.description,
      effects: item.effects?.map((effect: string) => {
        return effect.replace(/{passiveImg:([a-zA-Z0-9_]+)}/g, (match, p1) => {
          return (
            '<img src="' +
            page.props.app.cdn_url +
            "/assets/en/layout/en/image/battle/skill_dialog/passive_skill_dialog_" +
            replacements[p1] +
            '.png" alt="' +
            p1 +
            '" class="h-3 inline mb-0.5" />'
          );
        });
      }),
    };
  });
});

const transformation = computed(() => {
  return passiveSkill.value.effects.find(
    (e) =>
      e.transformation.description !== null || e.transformation.type !== null,
  )?.transformation;
});

const transformationType = computed(() => {
  if (!transformation.value.description) {
    return null;
  }

  const description = transformation.value?.description.replace(/[\r\n]+/g, "");

  let type =
    transformation.value?.type ??
    description.match(/into (([A-Z][a-z]+\s?)+)/)?.[1] ??
    description.match(/Breaks (([A-Z][a-z]+\s?)+)/)?.[1] ??
    description.split(" ")[0] ??
    "Transformation";

  return type === "Rages" ? "Rage" : type;
});

const domain = computed(() => {
  return props.character.passive_skill.effects.find((e) => e.domain !== null)
    ?.domain;
});
</script>

<template>
  <BaseCard class="!gap-0 divide-y divide-green-900/75 !p-0">
    <template #heading>Passive Skill</template>

    <template #headerActions>
      <BaseSwitch
        v-if="eza && hasSeza"
        v-model="seza"
        class="float-right inline text-sm"
        left="EZA"
        right="SEZA"
      />
    </template>

    <BaseContent class="p-4">
      <template #header>{{ character.passive_skill.name }}</template>

      <div class="space-y-2">
        <BaseSubContent
          v-for="(item, i) in effectDescriptionsItemized"
          :key="i"
          :header="item.description"
        >
          <ul class="ml-4 list-disc space-y-1">
            <li v-for="(effect, j) in item.effects" :key="j" v-html="effect" />
          </ul>
        </BaseSubContent>
      </div>
    </BaseContent>

    <BaseContent v-if="transformation" :header="transformationType" class="p-4">
      {{ transformation.description }}
    </BaseContent>

    <CharacterDomain v-if="domain" :domain="domain" class="p-4" />
  </BaseCard>
</template>

<style scoped></style>
